import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PickerOption } from "@/lib/liveavatar/options";
import { tavus } from "./client";

/**
 * Tavus-supported conversation languages. Sent verbatim as
 * `properties.language` in the `create_conversation` payload — Tavus
 * expects the lowercase language name (e.g. `"spanish"`), not the ISO
 * code, so the option `id` is the exact string Tavus wants and the
 * `label` is a human-friendly display.
 */
export const TAVUS_LANGUAGES: PickerOption[] = [
  { id: "english", label: "English" },
  { id: "french", label: "French" },
  { id: "german", label: "German" },
  { id: "spanish", label: "Spanish" },
  { id: "portuguese", label: "Portuguese" },
  { id: "mandarin", label: "Chinese (Mandarin)" },
  { id: "japanese", label: "Japanese" },
  { id: "hindi", label: "Hindi" },
  { id: "italian", label: "Italian" },
  { id: "korean", label: "Korean" },
  { id: "dutch", label: "Dutch" },
  { id: "polish", label: "Polish" },
  { id: "russian", label: "Russian" },
  { id: "swedish", label: "Swedish" },
  { id: "turkish", label: "Turkish" },
  { id: "filipino", label: "Filipino" },
  { id: "bulgarian", label: "Bulgarian" },
  { id: "romanian", label: "Romanian" },
  { id: "arabic", label: "Arabic" },
  { id: "czech", label: "Czech" },
  { id: "greek", label: "Greek" },
  { id: "finnish", label: "Finnish" },
  { id: "croatian", label: "Croatian" },
  { id: "malay", label: "Malay" },
  { id: "slovak", label: "Slovak" },
  { id: "danish", label: "Danish" },
  { id: "tamil", label: "Tamil" },
  { id: "ukrainian", label: "Ukrainian" },
  { id: "hungarian", label: "Hungarian" },
  { id: "norwegian", label: "Norwegian" },
  { id: "vietnamese", label: "Vietnamese" },
  { id: "bengali", label: "Bengali" },
  { id: "thai", label: "Thai" },
  { id: "hebrew", label: "Hebrew" },
  { id: "georgian", label: "Georgian" },
  { id: "indonesian", label: "Indonesian" },
  { id: "telugu", label: "Telugu" },
  { id: "gujarati", label: "Gujarati" },
  { id: "kannada", label: "Kannada" },
  { id: "malayalam", label: "Malayalam" },
  { id: "marathi", label: "Marathi" },
  { id: "punjabi", label: "Punjabi" },
  { id: "swahili", label: "Swahili" },
];

const SUPPORTED_BY_ID = new Map(TAVUS_LANGUAGES.map((l) => [l.id, l]));

/** Normalizes a catalog language value (`"en"`, `"English"`, `"en-US"`) to a Tavus id. */
function toTavusId(raw: string): string | undefined {
  const v = raw.trim().toLowerCase();
  if (!v) return undefined;
  if (SUPPORTED_BY_ID.has(v)) return v;
  const base = v.split(/[-_]/)[0];
  try {
    const dn = new Intl.DisplayNames(["en"], { type: "language" });
    const name = dn.of(base)?.toLowerCase();
    if (name && SUPPORTED_BY_ID.has(name)) return name;
  } catch {
    /* ignore */
  }
  return undefined;
}

/**
 * Conversation languages offered for Tavus. The list is derived from the
 * voices Tavus actually exposes (`/v2/voices`), intersected with the
 * languages Tavus documents for `create_conversation`, so a learner can
 * never pick something the runtime rejects. Falls back to the documented
 * list when the catalog carries no language metadata.
 */
export function useTavusLanguages(opts?: { enabled?: boolean }): {
  options: PickerOption[];
  isLoading: boolean;
  error: Error | null;
} {
  const q = useQuery({
    queryKey: ["tavus", "voices"],
    queryFn: () => tavus.listVoices(),
    staleTime: 5 * 60 * 1000,
    enabled: opts?.enabled ?? true,
  });
  const options = useMemo<PickerOption[]>(() => {
    const ids = new Set<string>();
    for (const v of q.data?.voices ?? []) {
      const raw = typeof v.language === "string" ? v.language : "";
      const id = raw ? toTavusId(raw) : undefined;
      if (id) ids.add(id);
    }
    if (ids.size === 0) return TAVUS_LANGUAGES;
    return TAVUS_LANGUAGES.filter((l) => ids.has(l.id));
  }, [q.data]);
  return { options, isLoading: q.isLoading, error: q.error as Error | null };
}
